/*
 * Copyright (c) 2026, King Living
 * SPDX-License-Identifier: BSD-3-Clause
 *
 * KL-1105 — Single source of truth for payment-method visibility.
 *
 * Payment-method display is driven by Business Manager's per-site
 * active/inactive status, surfaced through the SCAPI Shopper Baskets
 * `getPaymentMethodsForBasket` endpoint (the applicable payment methods for the
 * current basket). This replaces the previous mix of a custom `stripeEnabled`
 * site preference and an always-on PayPal option.
 *
 * The query runs asynchronously and is keyed on `basketId` (via
 * `usePaymentMethodsForBasket`), so it does not block the checkout UI and
 * refetches when the basket changes.
 */

import {useMemo} from 'react';

import {usePaymentMethodsForBasket} from '@salesforce/commerce-sdk-react';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {PAYPAL_PAYMENT_METHOD_ID} from '@kingliving/paypal-pwa/app/paypal-constants';

/** BM payment method id for the Stripe PaymentElement (card + APMs + wallets). */
export const STRIPE_PAYMENT_METHOD_ID = 'STRIPE_PAYMENT_ELEMENT';

/**
 * Resolve which payment methods are active in Business Manager for the current
 * basket, and derive Stripe / PayPal availability for the checkout UI.
 *
 * @param {string} [basketId] - Current basket id; the query is disabled until present.
 * @returns {{
 *   applicablePaymentMethodIds: Set<string>,
 *   isStripeMethodActive: boolean,
 *   isPayPalMethodActive: boolean,
 *   isStripeAvailable: boolean,
 *   isPayPalAvailable: boolean,
 *   isPaymentMethodsLoading: boolean,
 *   isPaymentMethodsResolved: boolean,
 * }}
 */
export function useBasketPaymentMethods(basketId) {
  const {site} = useMultiSite();
  const enabled = Boolean(basketId);

  const {data, isLoading} = usePaymentMethodsForBasket(
    {parameters: {basketId}},
    {enabled}
  );

  return useMemo(() => {
    const applicable = data?.applicablePaymentMethods ?? [];
    const ids = new Set(applicable.map((method) => method?.id).filter(Boolean));

    const isStripeMethodActive = ids.has(STRIPE_PAYMENT_METHOD_ID);
    const isPayPalMethodActive = ids.has(PAYPAL_PAYMENT_METHOD_ID);

    // Stripe additionally needs a publishable key to mount `<Elements>`; without
    // it the PaymentElement cannot render even if the BM method is active.
    const publishableKey = getConfig()?.app?.stripe?.publishableApiKeyBySite?.[site?.id];

    return {
      applicablePaymentMethodIds: ids,
      isStripeMethodActive,
      isPayPalMethodActive,
      isStripeAvailable: isStripeMethodActive && Boolean(publishableKey),
      isPayPalAvailable: isPayPalMethodActive,
      // Loading only counts when we actually queried (a basket exists).
      isPaymentMethodsLoading: enabled && isLoading,
      // Resolved = we have a definitive applicable-methods answer for this basket.
      isPaymentMethodsResolved: enabled && !isLoading,
    };
  }, [data, isLoading, enabled, site?.id]);
}

export default useBasketPaymentMethods;
